/**
 * The Billing page's one disclosure (TRA-1380). A table shows its first
 * `initial` rows and the control under it reveals the rest; an `initial`
 * of 0 keeps the whole table behind the control. A list that already fits
 * renders as it is, with no control, so its own empty state still shows.
 */

import { useState } from 'react'
import { ChevronRight } from 'lucide-react'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'

/** Rows a long table shows before the reader asks for the rest. */
export const PREVIEW_ROWS = 5

interface ShowMoreProps<Row> {
  rows: Row[]
  initial: number
  /** What the rows are, for the control's accessible name: "rates". */
  noun: string
  children: (visible: Row[]) => React.ReactNode
}

export function ShowMore<Row>({ rows, initial, noun, children }: ShowMoreProps<Row>): React.JSX.Element {
  const [open, setOpen] = useState(false)
  if (rows.length <= initial) return <>{children(rows)}</>
  const label = open ? 'Show less' : 'Show more'
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="space-y-3">
      {!open && initial > 0 && children(rows.slice(0, initial))}
      <CollapsibleContent>{children(rows)}</CollapsibleContent>
      <CollapsibleTrigger
        aria-label={`${label} ${noun}`}
        className="group flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronRight
          className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-90"
          aria-hidden="true"
        />
        {label}
      </CollapsibleTrigger>
    </Collapsible>
  )
}
